<template>
	<v-app>
		<NavigationDrawer v-if="isLoggedIn && !loading" />
		<ModalManager v-if="!loading" />
		<v-main class="content bg-background">
			<Loading v-if="loading" style="height: 100%"> </Loading>
			<LegacySyncUpdateBanner v-if="isLoggedIn && !loading" />
			<ContentFrame v-if="!loading" :dense="false">
				<template v-if="isLoggedIn && $vuetify.display.mdAndUp" #header>
					<Breadcrump />
				</template>
			</ContentFrame>
			<CookieConsent v-if="!loading"></CookieConsent>
			<v-snackbar
				v-if="isLoggedIn && !loading"
				v-model="isRefreshing"
				:timeout="-1"
				color="primary"
				multi-line
			>
				Ihre Anmeldung wird aktualisiert
				<template #actions>
					<v-progress-circular
						color="warning"
						indeterminate
					></v-progress-circular>
				</template>
			</v-snackbar>
			<UpdateDialog v-if="!loading" />
			<VueQueryDevtools v-if="!loading && !isCypress" />
			<Toast v-if="!loading" />
			<Errors v-if="isLoggedIn"></Errors>
		</v-main>
		<Footer />

		<TourFooter v-if="isLoggedIn" />
	</v-app>
</template>

<script setup lang="ts">
import ContentFrame from '@/components/elements/ContentFrame.vue'
import ModalManager from '@/components/elements/ModalManager.vue'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import Breadcrump from 'components/elements/Breadcrump.vue'
import NavigationDrawer from 'components/elements/Drawer.vue'
import Toast from 'components/elements/Toast.vue'
import UpdateDialog from 'components/modules/UpdateDialog.vue'
import LegacySyncUpdateBanner from 'components/elements/LegacySyncUpdateBanner.vue'
import Footer from 'elements/Footer.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { isCypress as isCypressEnv } from '@/helpers/isCypress'
import { storeToRefs } from 'pinia'
import { useAppSetup } from './composables/appSetup'
import { useLoadOfflineComponents } from './composables/loadOfflineComponents'
import { useConnectionStatus } from './composables/useConnectionStatus'
import { useAppTheme } from './composables/appTheme'
import { useTour } from './composables/useTour'
import { useAuthStore, useToastStore, useUserStore } from './stores/system'
import { takeLogoutReason } from './stores/system/user'
import { find } from 'lodash'
import { useI18n } from 'vue-i18n'
import CookieConsent from './components/elements/CookieConsent.vue'
import Loading from './components/templates/Loading.vue'
import Errors from './components/elements/Errors/Errors.vue'
import TourFooter from './components/elements/TourFooter.vue'
import { onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const { isRefreshing } = storeToRefs(useAuthStore())
const { isLoggedIn, loading } = storeToRefs(useUserStore())
const isPROD = import.meta.env.PROD
const isCypress = window.Cypress

// AAR-1482 test hook: expose the app's ACTUAL installed query client so
// Cypress can inspect the cache (page-query observer counts). useQueryClient()
// returns the client VueQueryPlugin installed, independent of how the plugin
// is wired — do not expose the named export from plugins/queryClient here.
if (isCypressEnv()) {
	;(window as any).__queryClient = useQueryClient()
}

useAppSetup()
useAppTheme()

// AAR-300/AAR-1677: Eine erzwungene Abmeldung (403 auf /whoami, etwa bei einem
// deaktivierten Teammitglied) beendet die Sitzung per Vollreload — der Toast, den der
// Aufrufer danach setzt, geht dabei verloren. Ausgerechnet der erklärt, warum der
// Zugang nicht mehr funktioniert. logout() hinterlegt den Grund, hier wird er nach dem
// Neuaufbau nachgereicht.
//
// Bewusst hier und nicht in Errors.vue: das hängt an `v-if="isLoggedIn"` und existiert
// auf der Anmeldeseite gar nicht — genau dort landet der Reload aber.
//
// Nachgeschlagen wird in der i18n-Tabelle wie in Error.vue, nicht in
// constants/errorMessages.ts: letztere ist bewusst rein deutsch (ShowError.vue rendert
// sie ohne Uebersetzung), und die Abmeldung landet auf der Anmeldeseite, wo die
// Sprache des Nutzers gelten muss. `invalid_subscription` gibt es mehrfach mit
// unterschiedlichem `code` — ohne den Code traefe der Lookup die falsche Zeile.
const { tm } = useI18n()

onMounted(() => {
	const reason = takeLogoutReason()
	if (!reason) return
	const messages = tm('component.error.errorMessages')
	const known =
		reason.error === 'invalid_subscription'
			? find(messages, { error: reason.error, code: reason.data?.code })
			: find(messages, { error: reason.error })
	if (!known) return
	useToastStore().toastError({
		header: known.header,
		message: known.message,
	})
})

// AAR-1670: connectivity signal for ShowError.vue, in ALL environments. Kept
// separate from useLoadOfflineComponents because that one also flips
// userStore.isOnline, which gates the not-yet-enabled query persister.
useConnectionStatus()

if (!isPROD) {
	useLoadOfflineComponents()
}

// Auto-start the end-user onboarding tour after first login completes.
const tour = useTour()
const route = useRoute()
let autoStartTimer: ReturnType<typeof setTimeout> | null = null
watch(
	[isLoggedIn, loading, () => route.name],
	([loggedIn, isLoading, routeName], previous) => {
		// Any pending auto-start is invalidated by ANY state change — the
		// timer's callback re-checks conditions anyway, but clearing here
		// avoids the brief window where the user navigates off the dashboard
		// while we're still queued and then yanked back.
		if (autoStartTimer) {
			clearTimeout(autoStartTimer)
			autoStartTimer = null
		}
		// Tear down any in-flight tour the moment the user logs out, so the
		// module-level currentDriver reference doesn't leak across sessions
		// in the same tab. Logout teardown must NOT mark the user as
		// "skipped" — that would suppress the next auto-start for them.
		const wasLoggedIn = previous?.[0]
		if (wasLoggedIn && !loggedIn) {
			tour.stop({ persist: false })
			return
		}
		if (!loggedIn || isLoading) return
		if (routeName !== 'dashboard') return
		if (!tour.shouldAutoStart()) return
		// Defer past the current paint so dashboard tiles can mount.
		autoStartTimer = setTimeout(() => {
			autoStartTimer = null
			// Re-check at fire-time — the user may have navigated away or
			// logged out during the wait.
			if (!isLoggedIn.value || loading.value) return
			if (route.name !== 'dashboard') return
			if (!tour.shouldAutoStart()) return
			tour.start()
		}, 400)
	},
	{ immediate: true }
)
</script>
<style lang="scss">
html {
	font-size: 15px;

	/* Design-Token für die Navigations-Schriftgröße (Haupt-Drawer).
	   rem-basiert → skaliert konsistent über Desktop/Tablet/Mobile.
	   Vuetify setzt für .v-list-item--nav nur 0.8125rem/1rem, was bei
	   font-size:15px effektiv ~12px ergibt und von Kunden als zu klein
	   empfunden wurde (AAR-1469). */
	--nav-item-font-size: 0.9375rem;
	--nav-item-line-height: 1.25rem;
}

/* Override scoped auf den Haupt-Navigations-Drawer (.nav).
   Spezifität 0-4-0 schlägt Vuetifys 0-2-0 ohne !important und trifft
   weder Kontextmenüs noch BuildingNav. Titel haben bereits
   ellipsis/nowrap → kein Layout-Overflow durch die größere Schrift. */
.v-navigation-drawer.nav .v-list-item--nav .v-list-item-title {
	font-size: var(--nav-item-font-size);
	line-height: var(--nav-item-line-height);
}

.v-label {
	font-size: 14px !important;
}

/* AAR-964 (erweitert): Icon-Buttons sind auf Touchscreens schwer zu treffen
   (~28–36px je nach size/density). Auf Mobile (xs) bekommen ALLE Icon-only-Buttons
   ein 44x44px-Tap-Target (WCAG 2.5.5 / HIG), ohne die Glyph-Größe zu verändern.
   Fixes width/height (kein min-*): Vuetify pinnt die Button-Größe via inline-grid
   + --v-btn-height, was min-width/min-height nicht überschreiben kann. Nur
   .v-btn--icon, damit Text-Buttons und dichte Desktop-Layouts unberührt bleiben. */
@media (max-width: 599.98px) {
	.v-btn--icon {
		width: 44px !important;
		height: 44px !important;
	}

	/* AAR-964 (Glyph): Vuetify verkleinert Icons in Buttons standardmäßig
	   (--v-icon-size-multiplier: 0.857 in Text-Buttons, 1 in Icon-Buttons).
	   Auf Mobile sollen die Glyphen in ALLEN Buttons größer/besser sichtbar
	   sein. Wir überschreiben den Multiplier einheitlich; .v-btn .v-icon matcht
	   jeden Button-Typ (Icon-only, Text+Icon, success/primary-Action-Buttons,
	   deren Label auf xs ausgeblendet ist). Wert ist die zentrale Stellschraube. */
	.v-btn .v-icon {
		--v-icon-size-multiplier: 1.3 !important;
	}
}

#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.scaled-container--width {
	max-width: 100%;

	&--xl {
		max-width: calc(100% * 0.6) !important;
	}

	&--lg {
		max-width: calc(100% * 0.6) !important;
	}
}

.light-grey--text {
	color: rgba(0, 0, 0, 0.6) !important;
}

.breakword {
	word-break: break-word;
}

.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg {
	background-color: rgba(0, 0, 0, 0.12) !important;
}

.stepperHeader {
	box-shadow: none !important;

	.v-stepper__label {
		text-shadow: none;
		font-size: 16px;
	}

	.v-stepper__step--active:not(.error--text) {
		font-weight: bold;

		.v-stepper__step__step {
			background-color: var(--v-secondary-base) !important;
		}
	}
}

.limitAccess .v-label {
	font-size: 13px;
}

#greetingText p {
	margin-bottom: 0 !important;
	color: var(--v-text-base) !important;
}

.v-label,
p,
:not(td):not(.v-btn__content):not(.v-btn):not(a):not(.v-tooltip__content):not(
		.noteBadgeColor
	):not(.data-table__link):not(.v-alert__content)
	> span:not(.colorDefault):not(.v-stepper__step__step):not(.v-badge):not(
		.v-badge__badge
	):not(.v-chip__content):not(.v-chip),
:not(.v-toolbar__title) > b,
.headline:not(a):not(.colorDefault),
.sitePolicyContainer,
h2:not(.changed):not(.created) > span {
	color: var(--v-text-base);
}

.ql-picker-label,
.ql-picker-item {
	color: #000 !important;
}

.iziToast-body {
	font-family: Roboto, Helvetica, sans-serif;
}

.theme--light.v-list .v-list_item--link:hover,
.theme--light.v-list:not(.v-list--flat)
	.v-list-item:hover:not(.autocompleteTotal),
.theme--light.v-list .v-list-group__header:hover {
	background-color: transparent !important;
	color: var(--v-secondary-base) !important;
}

.disabled * {
	color: rgba(0, 0, 0, 0.38) !important;
}

.v-data-footer__pagination {
	margin: 0 !important;
}

.scrollable-content {
	max-height: calc(75vh);
}

/* Auf Mobile (xs/fullscreen) bestimmt die Flex-Spalte im BaseStepper die Höhe
   des Scrollbereichs; keine fixe vh-Höhe mehr, die hinter der Button-Leiste
   verschwand. */

.text--overflow-hidden {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* driver.js tour theming – aligned with Vuetify secondary (accent) and
   surface tokens so the popover always matches the tenant CI. */
.driver-popover.aaremo-tour-popover {
	background-color: rgb(var(--v-theme-surface));
	color: rgb(var(--v-theme-on-surface));
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	font-family: Roboto, Helvetica, sans-serif;
	max-width: 380px;
	padding: 0;

	.driver-popover-title {
		background-color: rgb(var(--v-theme-secondary));
		color: rgb(var(--v-theme-on-secondary));
		font-weight: 600;
		font-size: 15px;
		padding: 12px 16px;
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
		margin: 0;
	}

	.driver-popover-close-btn {
		color: rgb(var(--v-theme-on-secondary));
		opacity: 0.85;
		top: 8px;
		right: 8px;
		font-size: 18px;
		&:hover {
			opacity: 1;
			color: rgb(var(--v-theme-on-secondary));
		}
	}

	.driver-popover-description {
		padding: 16px;
		font-size: 14px;
		line-height: 1.5;
	}

	.driver-popover-footer {
		// Driver's built-in footer is unused — we inject our own
		// .aaremo-tour-footer-slot above it via onPopoverRender.
		display: none;
	}

	// driver.js builds the arrow with border-trick: 3 borders are
	// transparent and 1 is the popover bg color. We must only override the
	// VISIBLE side per direction; using `border-color` shorthand turns the
	// triangle into a filled square ("white pixel").
	// side-bottom → arrow sits at top of popover, points up → bottom border visible
	// side-top    → arrow sits at bottom of popover, points down → top border visible
	// side-left   → arrow sits at right of popover, points right → left border visible
	// side-right  → arrow sits at left of popover, points left → right border visible
	.driver-popover-arrow-side-bottom.driver-popover-arrow {
		border-bottom-color: rgb(var(--v-theme-surface));
	}
	.driver-popover-arrow-side-top.driver-popover-arrow {
		border-top-color: rgb(var(--v-theme-surface));
	}
	.driver-popover-arrow-side-left.driver-popover-arrow {
		border-left-color: rgb(var(--v-theme-surface));
	}
	.driver-popover-arrow-side-right.driver-popover-arrow {
		border-right-color: rgb(var(--v-theme-surface));
	}

	.aaremo-tour-footer-slot {
		padding: 0 16px 14px;
		// Defensive: driver.js' .driver-active *{pointer-events:none} can
		// out-win the .driver-popover *{pointer-events:auto} override in
		// some cascade orderings (browser-dependent). Force auto on every
		// descendant so the tour buttons remain clickable.
		pointer-events: auto !important;
		* {
			pointer-events: auto !important;
		}
	}

	// driver.js applies `.driver-popover * { font-family: ... }` which beats
	// FontAwesome's class rule on equal specificity (later cascade order).
	// Force FA Pro back onto every <i> / .v-icon inside our footer slot.
	.aaremo-tour-footer-slot i,
	.aaremo-tour-footer-slot .v-icon {
		font-family: 'Font Awesome 7 Pro' !important;
	}
}

.aaremo-tour-footer {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
}
</style>
